// src/pages/auth/Login.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const LANGUAGE_KEY = "zedro_academy_language";

const getInitialLanguage = () => {
  if (typeof window === "undefined") return "es";
  return localStorage.getItem(LANGUAGE_KEY) || "es";
};

const copy = {
  es: {
    title: "Nos alegra verte de nuevo",
    subtitle: "Es momento de continuar tu recorrido con intención.",
    email: "Correo electrónico",
    password: "Contraseña",
    showPassword: "Mostrar contraseña",
    loading: "Ingresando…",
    submit: "Entrar a ZEDRO",
    forgotText: "¿Olvidaste tu contraseña?",
    forgotLink: "Recupérala aquí",
    fallbackError: "Error al iniciar sesión. Inténtalo de nuevo.",
  },
  fr: {
    title: "Heureux de vous revoir",
    subtitle: "Il est temps de poursuivre votre parcours avec intention.",
    email: "Adresse courriel",
    password: "Mot de passe",
    showPassword: "Afficher le mot de passe",
    loading: "Connexion…",
    submit: "Accéder à ZEDRO",
    forgotText: "Mot de passe oublié ?",
    forgotLink: "Le réinitialiser ici",
    fallbackError: "Erreur lors de la connexion. Veuillez réessayer.",
  },
};

export default function Login() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();

  const [language, setLanguage] = useState(getInitialLanguage);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const t = copy[language] || copy.es;

  useEffect(() => {
    try {
      const storedNotice = sessionStorage.getItem("zedro_session_notice");
      if (storedNotice) {
        setNotice(storedNotice);
        sessionStorage.removeItem("zedro_session_notice");
      }
    } catch {}
  }, []);

  useEffect(() => {
    const syncLanguage = (event) => {
      const nextLanguage =
        event?.detail?.language ||
        localStorage.getItem(LANGUAGE_KEY) ||
        "es";

      setLanguage(nextLanguage);
    };

    window.addEventListener("zedro:languagechange", syncLanguage);
    window.addEventListener("storage", syncLanguage);

    return () => {
      window.removeEventListener("zedro:languagechange", syncLanguage);
      window.removeEventListener("storage", syncLanguage);
    };
  }, []);

  const onChange = (k) => (e) =>
    setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const user = await login(form.email.trim(), form.password);

      if (user?.onboarding_completed) {
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/welcome", { replace: true });
      }
    } catch (err) {
      const message = err?.message || t.fallbackError;
      setError(message);
    }
  };

  return (
    <main className="z-auth-page">
      <div className="z-auth-shell">
        <section className="z-auth-card">
          <div className="z-auth-kicker">ZEDRO Academy</div>

          <h1 className="z-auth-title">{t.title}</h1>

          <p className="z-auth-subtitle">{t.subtitle}</p>

          <form className="z-auth-form z-auth-stack" onSubmit={handleSubmit}>
            {notice && !error && (
              <div className="z-auth-alert">{notice}</div>
            )}

            {error && (
              <div className="z-auth-alert z-auth-alert--error">
                {error}
              </div>
            )}

            <input
              className="z-auth-input"
              type="email"
              placeholder={t.email}
              value={form.email}
              onChange={onChange("email")}
              required
              autoComplete="email"
            />

            <input
              className="z-auth-input"
              type={showPassword ? "text" : "password"}
              placeholder={t.password}
              value={form.password}
              onChange={onChange("password")}
              required
              autoComplete="current-password"
            />

            <label className="z-auth-check">
              <input
                type="checkbox"
                checked={showPassword}
                onChange={() => setShowPassword(!showPassword)}
              />
              <span>{t.showPassword}</span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="z-auth-btn"
            >
              {loading ? t.loading : t.submit}
            </button>
          </form>

          <div className="z-auth-help">
            {t.forgotText}{" "}
            <Link to="/forgot-password">{t.forgotLink}</Link>
          </div>
        </section>
      </div>
    </main>
  );
}